import { format } from "date-fns";
import { useMemo } from "react";
import { Button, Divider, Icon, Typography } from "@/components/atoms";
import { getDisabledPlanInfo } from "@/redux/apis/subscription/subscriptionLimits";
import { SubscriptionPlan, GetSubscriptionPlansResponse, ScheduledPlanChange } from "@/redux/apis/subscription/types";
import { clsx } from "@/utils";
import { formatCurrency } from "@/utils/formatCurrency";

interface SubscriptionCardProps {
  subscription: SubscriptionPlan;
  allSubscriptionData: GetSubscriptionPlansResponse;
  headerRef?: React.RefObject<HTMLDivElement>;
  coreRef?: React.RefObject<HTMLDivElement>;
  onViewDemo?: (plan: SubscriptionPlan) => void;
  currentPlan?: string;
  scheduledPlanChange?: ScheduledPlanChange;
  onDowngrade?: (subscriptionId: string) => void;
  onUpgrade?: (subscriptionId: string) => void;
  onSubscribe?: (subscriptionId: string) => void;
  onReactivate?: (subscriptionId: string) => void;
  onCancelScheduledChange?: () => void;
  showRecommended?: boolean;
  showMostPopular?: boolean;
  isChangingPlan?: boolean;
  isPendingCancellation?: boolean;
  cancellationDate?: string;
  hasNoSubscription?: boolean;
  hideActions?: boolean;
}

export const SubscriptionCard = ({
  subscription,
  allSubscriptionData,
  headerRef,
  coreRef,
  onViewDemo: _onViewDemo, // TODO: Uncomment usage when View Demo API is available
  currentPlan,
  scheduledPlanChange,
  onDowngrade,
  onUpgrade,
  onSubscribe,
  onReactivate,
  onCancelScheduledChange,
  showRecommended = false,
  showMostPopular = false,
  isChangingPlan = false,
  isPendingCancellation = false,
  cancellationDate,
  hasNoSubscription = false,
  hideActions = false,
}: SubscriptionCardProps) => {
  const isCurrentPlan = currentPlan === subscription.subscriptionId;
  const isScheduledPlan = scheduledPlanChange?.newSubscriptionId === subscription.subscriptionId;
  const hasScheduledChange = !!scheduledPlanChange;

  // Check if plan is temporarily disabled (e.g., Solo)
  const disabledPlanInfo = getDisabledPlanInfo(subscription.slug);
  const isPlanDisabled = disabledPlanInfo.disabled;

  // Get the current plan's fee for comparison
  const currentPlanFee = useMemo(() => {
    const currentPlanData = allSubscriptionData.subscriptions.find((sub) => sub.subscriptionId === currentPlan);
    return currentPlanData?.subscriptionFee ?? 0;
  }, [allSubscriptionData, currentPlan]);

  // Determine which button to show
  const renderActionButton = () => {
    if (hideActions) return null;
    // This plan is scheduled to become active
    if (isScheduledPlan && scheduledPlanChange) {
      return (
        <div className="flex flex-col gap-2">
          <Button variant="primary" size="lg" disabled className="!border-info/30 !bg-info/10 !text-info">
            Scheduled for {format(new Date(scheduledPlanChange.effectiveDate), "MMM d, yyyy")}
          </Button>
          {onCancelScheduledChange && (
            <Button variant="tertiary" size="sm" onClick={onCancelScheduledChange} className="text-danger">
              Cancel Scheduled Change
            </Button>
          )}
        </div>
      );
    }

    // Current active plan
    if (isCurrentPlan) {
      // If pending cancellation, show Re-activate button (enabled)
      if (isPendingCancellation) {
        return (
          <div className="flex flex-col gap-1">
            <Button variant="primary" size="lg" onClick={() => onReactivate?.(subscription.subscriptionId)} disabled={isChangingPlan}>
              {isChangingPlan ? "Re-activating..." : "Re-activate"}
            </Button>
            {cancellationDate && (
              <Typography variant="small" className="text-center text-danger">
                Cancels on {format(new Date(cancellationDate), "MMM d, yyyy")}
              </Typography>
            )}
          </div>
        );
      }

      // Normal current plan display (not pending cancellation)
      return (
        <div className="flex flex-col gap-1">
          <Button variant="primary" size="lg" disabled>
            Current Plan
          </Button>
          {hasScheduledChange && scheduledPlanChange && (
            <Typography variant="small" className="text-center text-info">
              Changing to {scheduledPlanChange.newSubscriptionName} on {format(new Date(scheduledPlanChange.effectiveDate), "MMM d")}
            </Typography>
          )}
        </div>
      );
    }

    // Plan is temporarily disabled (e.g., Solo)
    if (isPlanDisabled) {
      return (
        <Button variant="primary" size="lg" disabled>
          {disabledPlanInfo.reason}
        </Button>
      );
    }

    // Other plans - always clickable (backend replaces existing schedule)
    const isDowngrade = subscription.subscriptionFee < currentPlanFee;

    // Downgrades are blocked while the subscription is scheduled to cancel — the
    // provider must re-activate first. (Upgrades still reactivate, so they stay
    // enabled.) Mirrors the backend guard in changeActivePlan.
    if (isDowngrade && isPendingCancellation) {
      return (
        <div className="flex flex-col gap-1">
          <Button variant="primary" size="lg" disabled>
            Downgrade Plan
          </Button>
          <Typography variant="small" className="text-center text-neutral-dark-gray">
            Re-activate your subscription to downgrade.
          </Typography>
        </div>
      );
    }

    // Determine button text based on subscription state
    const getButtonText = () => {
      if (isChangingPlan) {
        return hasNoSubscription ? "Subscribing..." : "Changing Plan...";
      }
      if (hasNoSubscription) return "Subscribe";
      return isDowngrade ? "Downgrade Plan" : "Upgrade Plan";
    };

    // Handle button click based on subscription state
    const handleClick = () => {
      if (hasNoSubscription) {
        onSubscribe?.(subscription.subscriptionId);
      } else if (isDowngrade) {
        onDowngrade?.(subscription.subscriptionId);
      } else {
        onUpgrade?.(subscription.subscriptionId);
      }
    };

    return (
      <Button variant="primary" size="lg" onClick={handleClick} disabled={isChangingPlan}>
        {getButtonText()}
      </Button>
    );
  };

  return (
    <div
      className={clsx(
        "relative flex flex-col rounded-xl border bg-white px-5 pb-8 pt-[30px] text-neutral-dark-gray",
        isCurrentPlan && isPendingCancellation ? "border-danger" : "border-neutral-mid-gray",
        isPlanDisabled && "opacity-50"
      )}
    >
      {/* Pending Cancellation Badge */}
      {isCurrentPlan && isPendingCancellation && (
        <div className="absolute -top-3.5 left-1/2 flex w-fit -translate-x-1/2 items-center justify-center rounded-full border border-danger bg-danger-light px-4 py-1 text-center">
          <Typography variant="title" className="text-danger">
            Pending Cancellation
          </Typography>
        </div>
      )}
      {/* Most Popular Badge */}
      {showMostPopular && !isPendingCancellation && (
        <div className="absolute -top-3.5 left-1/2 flex w-fit -translate-x-1/2 items-center justify-center rounded-full border border-orange bg-warning-light px-4 py-1 text-center">
          <Typography variant="title" className="text-orange">
            Most Popular
          </Typography>
        </div>
      )}

      {/* Header Section */}
      <div ref={headerRef}>
        {/* Recommended Badge */}
        {showRecommended && (
          <div className="absolute right-2.5 top-2.5 flex flex-row items-center gap-2 rounded-lg bg-neutral-surface-gray px-2 py-0.5">
            <Typography variant="action">Recommended</Typography>
            <Icon name="InfoCircle" size="md" variant="Bold" className="flex-shrink-0 rotate-180" />
          </div>
        )}

        <div className="flex flex-col">
          {/* Logo */}
          {subscription.iconSvg && (
            <div
              className="flex h-12 w-12 flex-shrink-0 [&>svg]:h-full [&>svg]:w-full"
              dangerouslySetInnerHTML={{ __html: subscription.iconSvg }}
            />
          )}

          {/* Plan Name and Description */}
          <div className="mt-2 flex flex-col">
            <Typography variant="h2">{subscription.subscriptionName}</Typography>
            <Typography
              variant="paragraph"
              className="[&_a]:text-info [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: subscription.subscriptionDescription ?? "" }}
            />
          </div>

          {/* Monthly Fee */}
          <div className="mt-8 flex flex-row items-center gap-2">
            <Typography variant="action" className="text-[48px]">
              {formatCurrency(subscription.subscriptionFee, 0, "AUD")}
            </Typography>
            <Typography variant="title">AUD /month</Typography>
          </div>

          {/* Trip-based pricing */}
          {(subscription.platformFeePerRide > 0 || subscription.offloadBookingFee > 0) && (
            <div className="flex flex-col gap-1">
              {subscription.platformFeePerRide > 0 && (
                <Typography variant="title">+ {formatCurrency(subscription.platformFeePerRide, 2, "AUD")} Per Trip</Typography>
              )}
              {subscription.offloadBookingFee > 0 && (
                <Typography variant="title">+ {formatCurrency(subscription.offloadBookingFee, 2, "AUD")} Per Offload</Typography>
              )}
            </div>
          )}

          {/* Setup Fee - Always show, even if $0 */}
          <div className="mt-4 flex flex-row items-center justify-between rounded-lg bg-neutral-surface-gray px-2.5 py-2">
            <Typography variant="paragraph2">One-time setup fee:</Typography>
            <Typography variant="title">{formatCurrency(subscription.setupFee, 2, "AUD")}</Typography>
          </div>

          {/* Secondary Description (feature checklist HTML) */}
          {subscription.subscriptionSecondaryDescription && (
            <div className="mt-4" dangerouslySetInnerHTML={{ __html: subscription.subscriptionSecondaryDescription }} />
          )}
        </div>
      </div>

      <Divider className="-mx-5 my-5" />

      {/* Core Features Section */}
      <div ref={coreRef}>
        <div className="flex flex-col gap-3">
          {renderActionButton()}
          {/* TODO: Uncomment when View Demo API is available */}
          {/* <Button variant="secondary" size="lg" onClick={() => onViewDemo?.(subscription)}>
            View Demo
          </Button> */}
        </div>
      </div>
    </div>
  );
};
